const React = require('react');
const { v4: uuid } = require('uuid');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { NavLink: Link } = require('react-router-dom');
const Avatar = require('components/Gamification/WeeklyAvatar');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: GifIcon } = require('@mui/icons-material/Gif');
const { default: TextField } = require('@mui/material/TextField');
const { default: SendIcon } = require('@mui/icons-material/Send');
const { default: ExpandLessIcon } = require('@mui/icons-material/ExpandLess');
const { default: ExpandMoreIcon } = require('@mui/icons-material/ExpandMore');
const { default: Paper } = require('@mui/material/Paper');
const { default: ArrowBack } = require('@mui/icons-material/ArrowBack');
const { default: Dialog } = require('@mui/material/Dialog');
const { default: DialogActions } = require('@mui/material/DialogActions');
const { default: DialogContent } = require('@mui/material/DialogContent');
const { default: DialogContentText } = require('@mui/material/DialogContentText');
const { default: Button } = require('@mui/material/Button');
const { default: DialogTitle } = require('@mui/material/DialogTitle');
const { default: Typography } = require('@mui/material/Typography');
const { default: List } = require('@mui/material/List');
const { default: ListItem } = require('@mui/material/ListItemButton');
const { default: ListItemText } = require('@mui/material/ListItemText');
const { default: MuiAccordion } = require('@mui/material/Accordion');
const { default: AccordionDetails } = require('@mui/material/AccordionDetails');
const { default: AccordionSummary } = require('@mui/material/AccordionSummary');
const { default: Collapse } = require('@mui/material/Collapse');
const { default: AddPhotoAlternateIcon } = require('@mui/icons-material/AddPhotoAlternate');
const { default: CancelIcon } = require('@mui/icons-material/Cancel');
const { default: Box } = require('@mui/material/Box');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const GroupHeader = require('../../classes/components/GroupHeader');
const { getSizedImageUrl } = require('utils/image');
const KlipyGifPicker = require('./KlipyGifPicker');

const PassionItem = require('components/PassionInterestItem');
const FocusIndicator = require('components/FocusIndicator');
const LoadingScreen = require('components/LoadingScreen');
const { transient$Props } = require('utils/styles');

const ChatList = require('./List');
const MentionsBase = require('../../containers/Chat/Mentions');
const ChatUtils = require('./utils');
const { default: Classes } = require('./styles.scss');
const MediaViewer = require('components/MediaViewer');
const SimpleDialog = require('components/SimpleDialog');
const { sendAnalyticsWithConfig, analyticsTemplates } = require('utils/analytics');
const NumUtils = require('utils/number');
const { runProfanityCheck } = require('utils/profanityFilter');
const { isSendKey } = require('utils/chatKeyboard');
const InappropriateLanguageDialog = require('./InappropriateLanguageDialog');
const { useTheme } = require('@mui/material/styles');

const ChatItem = require('./Item');

const {
    ELEMENT_IDS,
    MAX_CONTENT_WIDTH,
    MOBILE_MAX_CONTENT_WIDTH
} = require('utils/constants');

const { useResizeObserver } = require('hooks/useResizeObserver');
const { useChatConfirmationDialog } = require('./useChatConfirmationDialog');
const { useChatImageAttachment } = require('./useChatImageAttachment');
const { useChatLoading } = require('./useChatLoading');
const { usePeerGames } = require('components/Nerpy/usePeerGames');
const { PeerGameTools, PeerGameSummary, PeerGamesDialog } = require('components/Nerpy/PeerGames');
const { useChatMentions } = require('./useChatMentions');
const { useChatMessageState } = require('./useChatMessageState');
const { useChatReadReceipts } = require('./useChatReadReceipts');
const { useDirectMessageSimilarityText } = require('./useDirectMessageSimilarityText');

const {
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    useCallback
} = React;

const MESSAGE_CHAR_LIMIT = 1600;
const WARNING_THRESHOLD = 10;

const ChatPresentation = require('./Presentation');

const internals = {};

internals.EMPTY_ARRAY = Object.freeze([]);
internals.EMPTY_OBJECT = Object.freeze({});
internals.textFieldHeight = 48;
internals.chatInputWrapperVerticalPadding = 15;
internals.IconButton = Styled(IconButton)`
    padding: 4px !important;
`;

internals.requestMessages = async ({
    onRequestMessages,
    classId,
    sid,
    isAnnouncement,
    payload = {}
}) => {

    if (!onRequestMessages) {
        return null;
    }

    return onRequestMessages({
        classId,
        sid,
        isAnnouncement,
        ...(payload || {})
    });
};

internals.getSubmitMessagePayload = ({ isAnnouncement, message, sid, clientMessageId }) => {

    if (isAnnouncement) {
        return {
            classId: Number(sid),
            message
        };
    }

    return {
        channelSid: sid,
        message,
        clientMessageId
    };
};

const ChatFrame = function ChatFrame(props) {

    const {
        messages = internals.EMPTY_ARRAY,
        sid,
        onRequestMessages,
        onModerateMessage,
        onPinMessage,
        onRemoveMessage,
        onRemoveOwnMessage,
        onFlagInappropriate,
        onSendPrivateMessage,
        onSendConnectionRequest,
        onAcceptPeer,
        history,
        header,
        group = internals.EMPTY_OBJECT,
        users = internals.EMPTY_ARRAY,
        usersNameMap = internals.EMPTY_OBJECT,
        includeEveryoneMention,
        isOffline,
        rolePermissions: rolePermissionsProp,
        rolesInteractions = internals.EMPTY_ARRAY,
        showNotification,
        emptyChatInfo,
        isAnnouncement,
        classId,
        ga,
        useProfanityFilter,
        variant = 'group',
        conversationStarters,
        personalizedConversationStarters = false,
        // TODO see if we can replace this with var
        // currentUserBelongsToConversation
        userIsInClass,
        onSubmitMessage,
        userDetails,
        currentUser = internals.EMPTY_OBJECT,
        categories = internals.EMPTY_ARRAY,
        collapseHeaders,
        setCollapseHeaders,
        startListeningChannel,
        stopListeningChannel,
        getCurrentListeningChannel,
        onSaveMessage,
        hasUnreadMessages,
        onSetLastReadMessageIndex,
        setLastReadMessageIndex_byConversationSid,
        announcementConversation,
        onSetAnnouncementLastReadMessageIndex,
        hasPrevPage,
        onAddReaction,
        onRemoveReaction,
        fetchUserNames,
        onUploadChatImage,
        schoolEnableReactions,
        schoolEnableDmGifs,
        schoolEnableGroupGifs,
        canLoadChat = true,
        dependenciesReady = true,
        debug
    } = props;

    const rolePermissions = rolePermissionsProp || {};
    const { id: currentUserId } = currentUser;

    const {
        mentionMap,
        ChatInput,
        ChatWrapper,
        ChatFrameWrapper,
        AnnouncementInfoChatWrapper,
        Mentions,
        IconBackButton,
        AvatarWrapper,
        PassionsHolder,
        HeaderTextWrapper,
        HeaderText,
        HeaderTextAvatarLockup,
        ShowPinMessagesBtn,
        HeaderExpandIcon,
        IconCollapseButton,
        WarningDiv,
        ConversationStartersList,
        HeaderContainer,
        ScrollableChatArea,
        FooterContainer,
        PinnedMessagesWrapper,
        PinnedMessageHeader,
        EmptyChatInfoContainer,
        EmptyStartersContainer,
        EmptyStartersContent,
        EmptyChatInfoPill,
        ChatRetryButton,
        GifInstructionText
    } = internals;

    const [badWordsDialogOpen, setBadWordsDialogOpen] = useState(false);
    const [chatTextStateValue, setChatTextStateValue] = useState('');
    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [chatInputFocused, setChatInputFocused] = useState(false);
    const [badWordList, setBadWordList] = useState([]);
    const [isInitialRender, setIsInitialRender] = useState(true);

    const [gifPickerOpen, setGifPickerOpen] = useState(false);
    const [gifPickerKey, setGifPickerKey] = useState(0);
    const theme = useTheme();

    const otherUser = variant === 'dm' ? users.find(({ id }) => id !== currentUserId) : null;
    const mentionsRef = useRef(null);
    const inputRef = useRef(null);
    const headerRef = useRef(null);
    const footerRef = useRef(null);

    const headerSize = useResizeObserver(headerRef);
    const footerSize = useResizeObserver(footerRef);

    const showConversationStarters = variant === 'dm' && (conversationStarters?.length);

    const currentUserBelongsToConversation = users.find((user) => user.id === currentUserId);

    const userCanSendMessages = (variant === 'dm' && currentUserBelongsToConversation)
        || (!isAnnouncement && variant === 'group' && userIsInClass)
        || (isAnnouncement && rolePermissions.canPostInAnnouncement && userIsInClass);

    const games = usePeerGames({ user: currentUser, sid, enabled: variant === 'dm' && users.length === 2 && !!userCanSendMessages && dependenciesReady });
    const gameTools = games.enabled ? <PeerGameTools games={games} /> : null;
    const renderGameEvent = (event) => <PeerGameSummary event={event} games={games} userId={currentUserId} usersNameMap={usersNameMap} useProfanityFilter={useProfanityFilter} />;

    const messageReactionsEnabled = schoolEnableReactions && (!group || group.enableReactions);

    const gifPickerEnabled = (() => {

        if (variant === 'dm') {
            return schoolEnableDmGifs;
        }

        if (isAnnouncement) {
            return schoolEnableGroupGifs;
        }

        return schoolEnableGroupGifs;
    })();

    const otherFirstLastName = otherUser ? `${otherUser.firstName} ${otherUser.lastName}` : '';

    const remainingChars = MESSAGE_CHAR_LIMIT - chatTextStateValue.length;
    const isNearLimit = remainingChars <= WARNING_THRESHOLD && remainingChars > 0;
    const isOverLimit = remainingChars < 0;

    const {
        closeDialog,
        confirmDialog,
        dialog,
        openDialog,
        updateAlertFunction
    } = useChatConfirmationDialog();

    const {
        choosePic,
        imageInputRef,
        imagePreviewUrl,
        isConvertingImage,
        isUploadingImage,
        resetSelectedImage,
        selectedImageFile,
        setIsUploadingImage
    } = useChatImageAttachment({ showNotification });

    const {
        sendOptimisticMessage,
        optimisticMessagesLength,
        pinnedMessages,
        stableMessages
    } = useChatMessageState({
        currentUserId,
        sid: `${isAnnouncement ? 'announcement' : 'conversation'}:${sid}`,
        messages
    });

    const {
        mentionUsers,
        updateMentionUsers
    } = useChatMentions({ sid, users });

    const visiblePinnedMessages = pinnedMessages.filter((message) => ChatUtils.canViewMessage(message, {
        isModerator: rolePermissions.canPlaceInModeration
    }));

    const requestMessages = useCallback((payload = {}) => internals.requestMessages({
        onRequestMessages,
        classId,
        sid,
        isAnnouncement,
        payload
    }), [onRequestMessages, classId, sid, isAnnouncement]);

    const {
        isResolving: isResolvingInitialMessages,
        retry: retryInitialMessages,
        showEmpty: showEmptyChat,
        showError: showMessageLoadError,
        showMessages
    } = useChatLoading({
        canLoadChat,
        dependenciesReady,
        getCurrentListeningChannel,
        hasMessages: !!(messages.length || stableMessages.length),
        messages,
        onRequestMessages,
        requestMessages,
        showNotification,
        sid,
        startListeningChannel,
        stopListeningChannel
    });

    const similarityText = useDirectMessageSimilarityText({
        categories,
        dependenciesReady,
        userDetails,
        variant
    });

    const centerConversationStarters = internals.shouldCenterStarters({
        enabled: currentUser?.personalizedStartersEnabled,
        variant,
        showEmptyChat,
        hasMessages: Boolean(messages.length || stableMessages.length || games.summaries.length),
        canSend: Boolean(userCanSendMessages),
        hasStarters: Boolean(showConversationStarters)
    });

    useChatReadReceipts({
        announcementConversation,
        currentUserId,
        dependenciesReady,
        isAnnouncement,
        messages,
        onSetAnnouncementLastReadMessageIndex,
        onSetLastReadMessageIndex,
        setLastReadMessageIndexByConversationSid: setLastReadMessageIndex_byConversationSid,
        sid
    });

    // Wait for after first render, when refs are available
    useLayoutEffect(() => {

        const hiddenSiblings = inputRef.current?.parentNode?.getElementsByTagName('textarea') || [];

        for (let i = 0; i < hiddenSiblings.length; ++i) {
            hiddenSiblings[i].setAttribute('aria-label', 'Chat input');
        }
    }, []);

    const openSimpleDialogWithProps = openDialog;

    const updateMessageFromInput = useCallback((evt) => {

        setChatTextStateValue(evt.target.value);
    }, []);

    const setConversationStarterMsgToInput = useCallback((conversationStarterText) => {

        setChatTextStateValue(conversationStarterText);
    }, []);

    const updateMessageFromMention = useCallback((message, cb) => {

        setChatTextStateValue(message);
        cb?.();
    }, []);

    const sendMessage = useCallback((body) => {

        const clientMessageId = uuid();
        return sendOptimisticMessage({
            sid: `optimistic-${clientMessageId}`,
            id: `optimistic-${clientMessageId}`,
            clientMessageId,
            channelSid: isAnnouncement ? '' : sid,
            classId: isAnnouncement ? Number(sid) : group?.id,
            body,
            timestamp: new Date(),
            user: { ...currentUser, isMe: true },
            moderationStatus: 'ok',
            isPinned: false,
            isEdited: false
        }, () => onSubmitMessage(internals.getSubmitMessagePayload({
            isAnnouncement,
            message: body,
            sid,
            clientMessageId
        })));
    }, [sendOptimisticMessage, onSubmitMessage, sid, isAnnouncement, group?.id, currentUser]);

    const submitMessage = useCallback(async () => {

        const textValue = chatTextStateValue.trim();
        const hasText = textValue.length > 0;
        const hasImage = !!selectedImageFile;

        if (!hasText && !hasImage) {
            return;
        }

        if (hasText && textValue.length > MESSAGE_CHAR_LIMIT) {
            return showNotification(`Message is too long at ${NumUtils.format.commas(textValue.length)} characters. Max allowed is ${NumUtils.format.commas(MESSAGE_CHAR_LIMIT)}`);
        }

        if (hasText) {
            const { passesCheck, badPhrases } = runProfanityCheck(textValue);
            if (useProfanityFilter && (!passesCheck || badPhrases.length > 0)) {
                setBadWordsDialogOpen(true);
                setBadWordList(badPhrases);
                return false;
            }
        }

        if (hasImage) {
            setIsUploadingImage(true);

            try {
                const uploadPayload = {
                    chatImage: selectedImageFile,
                    channelSid: sid
                };
                const uploadResult = await onUploadChatImage(uploadPayload);

                const returnedImageUrl = uploadResult?.payload?.result?.imageUrl;

                if (returnedImageUrl) {
                    const imageMessage = JSON.stringify({
                        type: 'image',
                        url: returnedImageUrl,
                        preview: returnedImageUrl
                    });

                    await sendMessage(imageMessage);

                    resetSelectedImage();
                }
                else {
                    console.error('Invalid image URL or upload result:', uploadResult);
                    throw new Error('Invalid image URL or upload not returned from upload function.');
                }
            }
            catch (error) {

                console.error('Error uploading chat image:', error);
                showNotification('Failed to upload image. Please try again.');
                setIsUploadingImage(false);

                return;
            }
            finally {
                setIsUploadingImage(false);
            }
        }

        if (hasText) {
            const finalMessageBody = ChatUtils.editToFullBody(textValue, {
                mentionMap: mentionMap(mentionUsers, includeEveryoneMention)
            });

            setChatTextStateValue('');

            await sendMessage(finalMessageBody);
        }

        if (ga?.onSubmitMessage) {
            sendAnalyticsWithConfig(ga.onSubmitMessage);
        }

    }, [
        chatTextStateValue,
        selectedImageFile,
        isOffline,
        isAnnouncement,
        mentionUsers.length,
        mentionMap,
        includeEveryoneMention,
        sid,
        onSubmitMessage,
        onUploadChatImage,
        showNotification,
        useProfanityFilter,
        resetSelectedImage,
        currentUser,
        group?.id,
        ga?.onSubmitMessage,
        messages.length,
        sendMessage
    ]);

    const handleEnter = useCallback((evt) => {

        if (isSendKey(evt)) {
            evt.preventDefault();
            if (!mentionsRef.current?.isMentionsListOpen()) {
                submitMessage();
            }
        }
    }, [submitMessage]);

    const toggleCollapseHeaders = () => {

        setCollapseHeaders(!collapseHeaders);
        setTimeout(() => FocusIndicator.wrapFocusIndicatorForElement(), 10);
    };

    const handleBadWordAlertClose = useCallback(() => {

        setBadWordsDialogOpen(false);
        setBadWordList([]);
    }, []);

    const saveMessage = useCallback(({ editBody, ...rest }) => {

        if (editBody.length > MESSAGE_CHAR_LIMIT) {
            return showNotification(`Message is too large at ${NumUtils.format.commas(editBody.length)} characters. Max allowed is ${NumUtils.format.commas(MESSAGE_CHAR_LIMIT)}`);
        }

        const {
            passesCheck,
            badPhrases
        } = runProfanityCheck(editBody);

        if (useProfanityFilter && (!passesCheck || badPhrases.length > 0)) {
            setBadWordsDialogOpen(true);
            return false;
        }

        const {
            sid: messageSid,
            messageId,
            conversationId,
            classId: _classId
        } = rest;

        if (isAnnouncement) {
            return onSaveMessage({
                messageId: messageId.toString(),
                conversationId: conversationId.toString(),
                classId: _classId.toString(),
                body: ChatUtils.editToFullBody(editBody, {
                    mentionMap: mentionMap(mentionUsers, includeEveryoneMention)
                })
            });
        }

        return onSaveMessage({
            sid: messageSid,
            body: ChatUtils.editToFullBody(editBody, {
                mentionMap: mentionMap(mentionUsers, includeEveryoneMention)
            })
        });
    }, [
        isAnnouncement,
        sid,
        includeEveryoneMention,
        mentionUsers.length,
        useProfanityFilter
    ]);

    const handleOpenMediaViewer = useCallback((imageUrl) => {

        setSelectedImage(imageUrl);
        setMediaViewerOpen(true);
    }, []);

    const handleCloseMediaViewer = useCallback(() => {

        setMediaViewerOpen(false);
        setSelectedImage(null);
    }, []);

    const handleGifSelect = useCallback((gif) => {

        if (isOffline) {
            return;
        }

        const contentToCheck = [
            gif.description,
            ...(gif.raw?.tags || []),
            gif.imageUrl,
            gif.preview?.imageUrl
        ].filter(Boolean).join(' ').toLowerCase();

        if (contentToCheck) {
            const { passesCheck, badPhrases } = runProfanityCheck(contentToCheck);
            if (useProfanityFilter && (!passesCheck || badPhrases.length > 0)) {
                setBadWordsDialogOpen(true);
                setBadWordList(badPhrases);
                return;
            }
        }

        // Construct the message payload
        const message = {
            type: 'gif',
            url: gif.imageUrl,
            preview: gif.preview?.imageUrl
        };

        // Submit the message
        sendMessage(JSON.stringify(message));

        setGifPickerOpen(false);
    }, [
        isOffline,
        isAnnouncement,
        onSubmitMessage,
        sendMessage,
        sid,
        useProfanityFilter
    ]);

    return (
        <ChatFrameWrapper className={games.dialogOpen ? 'game-workspace-open' : ''}>
            <HeaderContainer ref={headerRef}>
                {variant === 'dm' && (
                    <Paper className={`${Classes.nameWrapper}`} elevation={0}>
                        <IconBackButton
                            aria-label='Go back'
                            onClick={history.goBack}
                            size='large'
                            data-focus-outline='radius:40,padding:-5'
                        >
                            <ArrowBack />
                        </IconBackButton>

                        <HeaderTextWrapper>
                            <HeaderTextAvatarLockup>
                                <AvatarWrapper>
                                    {otherUser && (
                                        <Link
                                            to={`/app/profile/${otherUser.id}`}
                                            className={`${Classes.avatarLink}`}
                                            data-focus-outline='radius:40,padding:2'
                                            aria-label={`${otherUser.firstName} ${otherUser.lastName}'s profile`}
                                        >
                                            <Avatar userId={otherUser?.id}
                                                className={Classes.avatarImg}
                                                src={getSizedImageUrl(otherUser.croppedPicture, 100)}
                                                alt={otherFirstLastName}
                                                size={60}
                                            />
                                        </Link>
                                    )}
                                </AvatarWrapper>

                                <HeaderText>
                                    {header || 'Messages'}
                                </HeaderText>
                            </HeaderTextAvatarLockup>
                        </HeaderTextWrapper>

                        <Collapse
                            in={!collapseHeaders}
                            timeout='auto'
                            unmountOnExit
                        >
                            {!!otherUser?.passionInterests?.length && (
                                <PassionsHolder>
                                    {otherUser.passionInterests.map((passion) => {

                                        const svg = passion.svg || categories.find(({ id }) => id === passion.categoryId)?.svg;

                                        if (svg) {
                                            return <PassionItem key={passion.id} interest={passion} svg={svg} />;
                                        }
                                    })}
                                </PassionsHolder>
                            )}
                            <p
                                className={`${Classes.similarityWrapper}`}
                                dangerouslySetInnerHTML={{ __html: similarityText }}
                            />
                        </Collapse>

                        <IconCollapseButton
                            onClick={() => setCollapseHeaders(!collapseHeaders)}
                            aria-label='Toggle collapse header'
                            data-focus-outline='radius:80'
                        >
                            <HeaderExpandIcon $headerIsCollapsed={collapseHeaders} />
                        </IconCollapseButton>
                    </Paper>
                )}

                {variant === 'group' && !!group?.id && !!rolePermissionsProp && (
                    <GroupHeader
                        group={group}
                        variant='chat'
                        openSimpleDialogWithProps={openSimpleDialogWithProps}
                    />
                )}

                {variant === 'group' && visiblePinnedMessages.length ? (
                    <Paper elevation={0} style={{ position: 'relative', backgroundColor: 'white', boxShadow: 'none', border: '1px solid rgba(18, 39, 79, 0.09)' }}>
                        <PinnedMessagesWrapper>
                            <PinnedMessageHeader>Pinned Messages</PinnedMessageHeader>
                            <ShowPinMessagesBtn
                                variant='text'
                                color='secondary'
                                aria-expanded={!collapseHeaders}
                                aria-label={collapseHeaders ? 'Show pinned messages' : 'Hide pinned messages'}
                                onClick={toggleCollapseHeaders}
                                startIcon={collapseHeaders ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                                data-focus-outline='radius:40,padding:3'
                            >
                                {collapseHeaders ? 'Show' : 'Hide'}
                            </ShowPinMessagesBtn>
                        </PinnedMessagesWrapper>

                        <div style={{ paddingBottom: 4 }}>
                            <Collapse
                                in={!collapseHeaders}
                                timeout='auto'
                                unmountOnExit
                            >
                                <div style={{ maxHeight: 280, overflow: 'auto' }}>
                                    {visiblePinnedMessages.map((message) => {

                                        return <div key={message.sid || message.id} className={`${Classes.pinnedMessageWrapper} contentWrapper`}>
                                            <ChatItem
                                                classId={classId}
                                                onSave={saveMessage}
                                                onModerate={onModerateMessage}
                                                onPin={onPinMessage}
                                                onRemove={onRemoveMessage}
                                                onRemoveOwn={onRemoveOwnMessage}
                                                onFlagInappropriate={onFlagInappropriate}
                                                updateAlertFunction={updateAlertFunction}
                                                showModerationControls={true}
                                                showNotification={false}
                                                rolesInteractions={rolesInteractions}
                                                rolePermissions={rolePermissions}
                                                className={Classes.cf}
                                                message={message}
                                                isAnnouncement={isAnnouncement}
                                                onAddReaction={onAddReaction}
                                                onRemoveReaction={onRemoveReaction}
                                                usersNameMap={usersNameMap}
                                            />
                                        </div>;
                                    })}
                                </div>
                            </Collapse>
                        </div>
                    </Paper>
                ) : null}
            </HeaderContainer>

            <ScrollableChatArea>
                {isResolvingInitialMessages && (
                    <LoadingScreen variant='content' />
                )}

                {(showMessages || (dependenciesReady && games.summaries.length > 0)) && (
                    <ChatList
                        classId={classId}
                        userId={currentUser.id}
                        usersNameMap={usersNameMap}
                        onAddReaction={onAddReaction}
                        onRemoveReaction={onRemoveReaction}
                        isInitialRender={isInitialRender}
                        setIsInitialRender={setIsInitialRender}
                        headerHeight={headerSize.height}
                        footerHeight={footerSize.height}
                        isAnnouncement={isAnnouncement}
                        isGroupChat={variant === 'group'}
                        messages={stableMessages}
                        gameEvents={games.summaries}
                        renderGameEvent={renderGameEvent}
                        optimisticMessagesLength={optimisticMessagesLength}
                        onRequestMessages={requestMessages}
                        onSaveMessage={saveMessage}
                        onModerateMessage={onModerateMessage}
                        onPinMessage={onPinMessage}
                        onRemoveMessage={onRemoveMessage}
                        onRemoveOwnMessage={onRemoveOwnMessage}
                        onFlagInappropriate={onFlagInappropriate}
                        onSendPrivateMessage={onSendPrivateMessage}
                        onSendConnectionRequest={onSendConnectionRequest}
                        onAcceptPeer={onAcceptPeer}
                        openSimpleDialogWithProps={openSimpleDialogWithProps}
                        showNotification={showNotification}
                        rolesInteractions={rolesInteractions}
                        rolePermissions={rolePermissions}
                        showModerationControls={rolePermissions.canPlaceInModeration}
                        hasPrevPage={hasPrevPage}
                        enableReactions={messageReactionsEnabled}
                        updateAlertFunction={updateAlertFunction}
                        debug={debug}
                        onOpenMediaViewer={handleOpenMediaViewer}
                    />
                )}

                {centerConversationStarters && (
                    <EmptyStartersContainer
                        $headerHeight={headerSize.height}
                        $footerHeight={footerSize.height}
                    >
                        <EmptyStartersContent>
                            {emptyChatInfo && <EmptyChatInfoPill role='status'>{emptyChatInfo}</EmptyChatInfoPill>}
                            <ConversationStartersList
                                centered
                                conversationStarters={conversationStarters}
                                personalized={personalizedConversationStarters}
                                onItemClick={setConversationStarterMsgToInput}
                            />
                        </EmptyStartersContent>
                    </EmptyStartersContainer>
                )}

                {showEmptyChat && !games.summaries.length && emptyChatInfo && !centerConversationStarters && (
                    <EmptyChatInfoContainer
                        $headerHeight={headerSize.height}
                        $footerHeight={footerSize.height}
                    >
                        <EmptyChatInfoPill role='status' aria-live='polite'>
                            {emptyChatInfo}
                        </EmptyChatInfoPill>
                    </EmptyChatInfoContainer>
                )}

                {showMessageLoadError && (
                    <EmptyChatInfoContainer
                        $interactive
                        $headerHeight={headerSize.height}
                        $footerHeight={footerSize.height}
                    >
                        <EmptyChatInfoPill role='alert'>
                            <span>We couldn’t load this conversation.</span>
                            <ChatRetryButton onClick={retryInitialMessages} variant='outlined'>
                                Retry
                            </ChatRetryButton>
                        </EmptyChatInfoPill>
                    </EmptyChatInfoContainer>
                )}
            </ScrollableChatArea>

            <FooterContainer ref={footerRef}>
                {dependenciesReady && <React.Fragment>
                {(userCanSendMessages) ? <div>
                    {(imagePreviewUrl || isUploadingImage || isConvertingImage) && (
                        <Box sx={{
                            padding: '8px 15px 0 15px',
                            backgroundColor: 'white',
                            position: 'relative',
                            border: '1px solid rgba(18, 39, 79, 0.12)',
                            borderBottom: '1px solid rgba(18, 39, 79, 0.08)',
                            borderTopLeftRadius: '16px',
                            borderTopRightRadius: '16px',
                            boxShadow: 'none'
                        }}>
                            <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                                {(isUploadingImage || isConvertingImage) && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0, left: 0, right: 0, bottom: 0,
                                        background: 'rgba(255,255,255,0.7)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        borderRadius: '4px',
                                        zIndex: 1
                                    }}>
                                        <CircularProgress size={24} />
                                        {isConvertingImage && !isUploadingImage && (
                                            <Typography variant="caption" sx={{ mt: 0.5, color: '#555' }}>Processing...</Typography>
                                        )}
                                        {isUploadingImage && (
                                            <Typography variant="caption" sx={{ mt: 0.5, color: '#555' }}>Uploading...</Typography>
                                        )}
                                    </Box>
                                )}
                                <img
                                    src={imagePreviewUrl}
                                    alt="Preview"
                                    style={{
                                        maxHeight: '80px',
                                        maxWidth: '100%',
                                        display: 'block',
                                        borderRadius: '4px',
                                        opacity: (isUploadingImage || isConvertingImage) ? 0.6 : 1
                                    }}
                                />
                                {!isUploadingImage && !isConvertingImage && imagePreviewUrl && (
                                    <IconButton
                                        size="small"
                                        onClick={resetSelectedImage}
                                        sx={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                            color: 'white',
                                            padding: '2px',
                                            zIndex: 2,
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.7)'
                                            }
                                        }}
                                        aria-label="Remove image"
                                    >
                                        <CancelIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    )}
                    <Mentions
                        ref={mentionsRef}
                        className={`${Classes.mentionListWrapper} contentWrapper`}
                        message={chatTextStateValue}
                        $footerHeight={footerSize.height}
                        inputEl={inputRef.current}
                        users={users}
                        classId={classId}
                        variant={variant}
                        onRequestChange={updateMessageFromMention}
                        onSearchFinish={updateMentionUsers}
                        includeEveryoneMention={includeEveryoneMention}
                    />
                    {(isNearLimit || isOverLimit) && (
                        <WarningDiv isOverLimit={isOverLimit}>
                            {isOverLimit
                                ? `Message is too long. Please remove ${Math.abs(remainingChars)} characters.`
                                : `${remainingChars} characters remaining`}
                        </WarningDiv>
                    )}
                    <input
                        ref={imageInputRef}
                        type='file'
                        accept='image/*,.heic,.heif'
                        aria-label='Chat Image Input'
                        onChange={choosePic}
                        style={{ display: 'none' }}
                        id='chat-image-input'
                    />
                    <ChatWrapper
                        $isOffline={!!isOffline}
                        $chatInputFocused={chatInputFocused}
                        style={{ borderTopLeftRadius: (imagePreviewUrl || isUploadingImage || isConvertingImage) ? 0 : '16px', borderTopRightRadius: (imagePreviewUrl || isUploadingImage || isConvertingImage) ? 0 : '16px' }}
                    >
                        {rolePermissions.canSendImages && (
                            <IconButton
                                disabled={isOffline || isUploadingImage || isConvertingImage}
                                onClick={() => imageInputRef.current && imageInputRef.current.click()}
                                aria-label='add image'
                                data-focus-outline='radius:60,padding:-6'
                            >
                                <AddPhotoAlternateIcon
                                    style={{
                                        fontSize: '2.5rem',
                                        color: (isOffline || isUploadingImage || isConvertingImage)
                                            ? 'grey'
                                            : theme.palette.primary.main
                                    }}
                                />
                            </IconButton>
                        )}
                        <ChatInput
                            id='chat-input'
                            aria-label='Chat input. For best typing experience, enter "Forms Mode" by pressing Control + Option + Shift + Down Arrow.'
                            placeholder='Type a message...'
                            multiline
                            minRows={1}
                            maxRows={3}
                            inputRef={inputRef}
                            disabled={isOffline}
                            value={chatTextStateValue}
                            onKeyDown={handleEnter}
                            onChange={updateMessageFromInput}
                            inputProps={{
                                style: {
                                    padding: `${internals.chatInputWrapperVerticalPadding}px 10px`
                                },
                                'aria-multiline': 'true',
                                role: 'textbox'
                            }}
                            onFocus={() => setChatInputFocused(true)}
                            onBlur={() => setChatInputFocused(false)}
                        />
                        {gifPickerEnabled && (
                            <IconButton
                                disabled={isOffline || isUploadingImage || isConvertingImage}
                                onClick={() => setGifPickerOpen(true)}
                                aria-label='add gif'
                                data-focus-outline='radius:60,padding:-6'
                            >
                                <GifIcon
                                    style={{
                                        color: (isOffline || isUploadingImage || isConvertingImage)
                                            ? 'grey'
                                            : theme.palette.primary.main, fontSize: '2.5rem'
                                    }}
                                />
                            </IconButton>
                        )}
                        <IconButton
                            disabled={isOffline || isOverLimit || (!chatTextStateValue.trim() && !selectedImageFile) || isUploadingImage || isConvertingImage}
                            onClick={submitMessage}
                            className={Classes.sendButton}
                            aria-label='send message'
                            size='large'
                            data-focus-outline='radius:60,padding:-6'
                        >
                            {(isUploadingImage || isConvertingImage)
                                ? (
                                    <CircularProgress
                                        size={24}
                                        sx={{ color: theme.palette.primary.main }}
                                    />
                                ) : (
                                    <SendIcon
                                        style={{
                                            color: (isOffline || isOverLimit || (!chatTextStateValue.trim() && !selectedImageFile))
                                                ? 'grey'
                                                : theme.palette.primary.main
                                        }}
                                    />
                                )
                            }
                        </IconButton>
                    </ChatWrapper>
                </div> : null}
                {showConversationStarters && userCanSendMessages && !centerConversationStarters ? (
                    <ConversationStartersList
                        conversationStarters={conversationStarters}
                        personalized={personalizedConversationStarters}
                        onItemClick={setConversationStarterMsgToInput}
                    />
                ) : null}
                {!userCanSendMessages ? <AnnouncementInfoChatWrapper
                    id={ELEMENT_IDS.chatTextfield}
                >
                    <Typography
                        tabIndex={0}
                        className={Classes.announcementInfoChatText}
                        align='center'
                        data-focus-outline='radius:10,padding:3'
                    >
                        {isAnnouncement ? 'Announcements Only' : 'View-only Mode: join the group to chat'}
                    </Typography>
                </AnnouncementInfoChatWrapper> : null}
                </React.Fragment>}
                {gameTools}
            </FooterContainer>
            <PeerGamesDialog games={games} userId={currentUserId} usersNameMap={usersNameMap} useProfanityFilter={useProfanityFilter} />

            <SimpleDialog
                isOpen={dialog.isOpen}
                onRequestClose={closeDialog}
                title={dialog.title}
                content={dialog.content}
                buttons={<React.Fragment>
                    {!dialog.hideDecline && <Button
                        onClick={closeDialog}
                        color='secondary'
                        variant='contained'
                        data-focus-outline='radius:40'
                    >
                        {dialog.declineLabel}
                    </Button>}
                    <Button
                        onClick={confirmDialog}
                        color='primary'
                        variant='contained'
                        data-focus-outline='radius:40'
                    >
                        {dialog.confirmLabel}
                    </Button>
                </React.Fragment>}
            />

            <InappropriateLanguageDialog open={badWordsDialogOpen} words={badWordList} onClose={handleBadWordAlertClose} />

            <Dialog
                open={gifPickerOpen}
                onClose={() => setGifPickerOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Send a GIF
                </DialogTitle>
                <DialogContent>
                    <KlipyGifPicker
                        key={gifPickerKey}
                        onGifClick={handleGifSelect}
                        width='100%'
                        height={400}
                        contentFilter='high'
                        useProfanityFilter={useProfanityFilter}
                        setBadWordsDialogOpen={setBadWordsDialogOpen}
                        setBadWordList={setBadWordList}
                    />
                </DialogContent>
                <DialogActions>
                    <GifInstructionText>
                        Tap GIF to send
                    </GifInstructionText>
                    <Button onClick={() => setGifPickerOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <MediaViewer
                isOpen={mediaViewerOpen}
                onClose={handleCloseMediaViewer}
                imageUrl={selectedImage}
                imageAlt="Chat image"
            />
        </ChatFrameWrapper>
    );
};

ChatFrame.propTypes = {
    history: T.object.isRequired,
    onSubmitMessage: T.func.isRequired,
    onSaveMessage: T.func.isRequired,
    onModerateMessage: T.func.isRequired,
    onPinMessage: T.func.isRequired,
    onRemoveMessage: T.func.isRequired,
    onRemoveOwnMessage: T.func.isRequired,
    onFlagInappropriate: T.func.isRequired,
    onSendPrivateMessage: T.func,
    onSendConnectionRequest: T.func,
    onAcceptPeer: T.func,
    messages: T.array.isRequired,
    group: T.object.isRequired,
    users: T.array,
    usersNameMap: T.object,
    userDetails: T.object,
    header: T.string,
    emptyChatInfo: T.node,
    announcementConversation: T.object,
    onSetAnnouncementLastReadMessageIndex: T.func,
    setLastReadMessageIndex_byConversationSid: T.func,
    hasUnreadMessages: T.bool.isRequired,
    onSetLastReadMessageIndex: T.func,
    onRequestMessages: T.func,
    ga: T.shape({
        onSubmitMessage: T.array,
        onTypingMessage: T.array
    }),
    isOffline: T.bool,
    includeEveryoneMention: T.bool,
    rolePermissions: T.shape({
        id: T.number,
        roleId: T.number,
        schoolId: T.number,
        homeText: T.string,
        canNotify: T.bool,
        canBatchNotify: T.bool,
        canPreapprove: T.bool,
        canDisable: T.bool,
        canCreateGroups: T.bool,
        canEditGroups: T.bool,
        canManageGroups: T.bool,
        canPlaceInModeration: T.bool,
        canPostInAnnouncement: T.bool,
        canSeeChatMessagesNoJoin: T.bool,
        canSendImages: T.bool
    }),
    rolesInteractions: T.arrayOf(T.shape({
        id: T.number,
        name: T.string,
        label: T.string,
        schoolId: T.number,
        canViewProfile: T.bool,
        canViewInGroup: T.bool,
        canChat: T.bool,
        canChatWithoutConnection: T.bool,
        canSeeConnections: T.bool,
        canSeeUsersGroups: T.bool,
        canSeeSurveyFields: T.bool,
        canSeeExtendedProfile: T.bool,
        allowAutoConnect: T.bool
    })),
    role: T.shape({
        id: T.number,
        name: T.string,
        label: T.string
    }),
    showNotification: T.func,
    sid: T.string,
    variant: T.oneOf(['dm', 'group']),
    classId: T.any,
    isAnnouncement: T.bool,
    useProfanityFilter: T.bool,
    conversationStarters: T.arrayOf(T.shape({
        id: T.oneOfType([T.number, T.string]),
        starterText: T.string,
        schoolId: T.number
    })),
    personalizedConversationStarters: T.bool,
    userIsInClass: T.bool,
    currentUser: T.object,
    categories: T.array,
    setCollapseHeaders: T.func,
    collapseHeaders: T.bool,
    getCurrentListeningChannel: T.func.isRequired,
    startListeningChannel: T.func.isRequired,
    stopListeningChannel: T.func.isRequired,
    hasPrevPage: T.bool,
    onAddReaction: T.func,
    onRemoveReaction: T.func,
    fetchUserNames: T.func.isRequired,
    onUploadChatImage: T.func.isRequired,
    schoolEnableGroupGifs: T.bool,
    schoolEnableReactions: T.bool,
    schoolEnableDmGifs: T.bool,
    canLoadChat: T.bool,
    dependenciesReady: T.bool,
    debug: T.bool
};

if (process.env.NODE_ENV === 'test') {
    ChatFrame._internals = internals;
}

module.exports = ChatFrame;

internals.ConversationStartersList = require('./ConversationStarters');

internals.shouldCenterStarters = ({ enabled, variant, showEmptyChat, hasMessages, canSend, hasStarters }) =>
    enabled === true && variant === 'dm' && showEmptyChat && !hasMessages && canSend && hasStarters;

internals.mentionMap = (users, includeEveryoneMention) => {

    const mentionMap = (users || []).reduce((collector, { id, firstName, lastName }) => {

        const name = [firstName, lastName].filter((x) => !!x).join(' ');

        return {
            ...collector,
            [name]: id
        };
    }, {});

    if (includeEveryoneMention) {
        mentionMap.everyone = null;
    }

    return mentionMap;
};

internals.ChatFrameWrapper = Styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;

    &.game-workspace-open { padding-right: min(520px, 50vw); box-sizing: border-box; }

    @media (max-width: 600px) {
        left: 0;
        width: 100%;
        &.game-workspace-open { padding-right: 0; }
    }

    .chat-loading,
    .loading,
    [class*="Loading"] {
        max-width: 180px;
        margin-left: auto;
        margin-right: auto;
        text-align: center;
    }

    .chat-loading img,
    .loading img,
    [class*="Loading"] img,
    img[alt*="Loading"],
    img[src*="loading"],
    img[src*="spinner"],
    .chat-loading svg,
    .loading svg,
    [class*="Loading"] svg {
        width: 96px !important;
        height: 96px !important;
        max-width: 96px !important;
        max-height: 96px !important;
        object-fit: contain;
    }
`;

internals.HeaderContainer = Styled.div`
    position: absolute;
    top: 0;
    width: 100%;
    z-index: 3;
    display: flex;
    flex-flow: column nowrap;
    left: 50%;
    transform: translate(-50%);
    max-width: ${MAX_CONTENT_WIDTH}px;
    box-sizing: border-box;

    > * {
        width: 100%;
        max-width: ${MAX_CONTENT_WIDTH}px;
        margin-left: auto;
        margin-right: auto;
        box-sizing: border-box;
    }

    @media (max-width: 600px) {
        max-width: ${MOBILE_MAX_CONTENT_WIDTH}px;
    }
`;

internals.ScrollableChatArea = Styled.main`
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
`;

internals.FooterContainer = Styled.div`
    position: absolute;
    bottom: 0;
    width: 100%;
    z-index: 3;
    left: 50%;
    transform: translate(-50%);
    max-width: ${MAX_CONTENT_WIDTH}px;
    box-sizing: border-box;

    > * {
        width: 100%;
        max-width: ${MAX_CONTENT_WIDTH}px;
        margin-left: auto;
        margin-right: auto;
        box-sizing: border-box;
    }

    @media (max-width: 600px) {
        max-width: ${MOBILE_MAX_CONTENT_WIDTH}px;
    }
`;

internals.ChatInput = ChatPresentation.ChatInput;

internals.ChatWrapper = ChatPresentation.ChatWrapper;

internals.AnnouncementInfoChatWrapper = Styled.div`
    min-height: 62px;
    width: 100%;
    display: flex;
    flex-flow: row nowrap;
    left: 0;
    bottom: 0;
    align-items: center;
    justify-content: center;
    background-color: #f7f7f7;
    border: 1px solid rgba(18, 39, 79, 0.12);
    border-bottom: none;
    box-shadow: none;
    border-top-left-radius: ${({ theme }) => theme.shape.borderRadius}px;
    border-top-right-radius: ${({ theme }) => theme.shape.borderRadius}px;
    box-sizing: border-box;
`;

internals.Mentions = Styled(MentionsBase, transient$Props)`
    position: absolute;
    bottom: ${({ $footerHeight }) => `${$footerHeight || 0}px`};
`;

internals.IconBackButton = ChatPresentation.IconBackButton;

internals.IconCollapseButton = ChatPresentation.IconCollapseButton;

internals.HeaderExpandIcon = ChatPresentation.HeaderExpandIcon;

internals.PassionsHolder = Styled.div`
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    padding-top: 10px;
    margin-bottom: -8px;
`;

internals.AvatarWrapper = Styled.div`
    left: 0;
    top: 5px;
    line-height: 1;
`;

internals.HeaderTextWrapper = ChatPresentation.HeaderTextWrapper;

internals.HeaderText = ChatPresentation.HeaderText;

internals.HeaderTextAvatarLockup = ChatPresentation.HeaderTextAvatarLockup;



internals.ShowPinMessagesBtn = Styled(Button)`
    position: absolute;
    top: 8px;
    left: calc(50% + 70px);
    padding: 3px 12px;
`;

internals.ChatItem = Styled.div`
    padding: 10px;
    border-bottom: 1px solid #e0e0e0;
`;









internals.WarningDiv = Styled.div`
    color: ${({ isOverLimit }) => isOverLimit ? 'red' : 'orange'};
    margin-bottom: 8px;
    font-weight: bold;
    z-index: 100000;
    margin-left: 6px;
    font-size: 12px;
    background: none;
`;

internals.PinnedMessagesWrapper = Styled.div`
    background-color: white;
    width: 100%;
    z-index: 1;
    align-items: center;
    position: relative;
    display: flex;

    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    left: 50%;
    transform: translate(-50%, 0);
    text-align: center;
    width: 80%;
`;

internals.PinnedMessageHeader = Styled.h4`
    text-align: center;
    flex: 1 0 auto;
    margin-block-start: 0.75rem;
    margin-block-end: 0.75rem;
`;

internals.EmptyChatInfoContainer = Styled('div', transient$Props)`
    position: absolute;
    top: ${({ $headerHeight }) => `${$headerHeight || 0}px`};
    right: 0;
    bottom: ${({ $footerHeight }) => `${$footerHeight || 0}px`};
    left: 0;
    z-index: 2;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 68px 20px 18px;
    pointer-events: ${({ $interactive }) => $interactive ? 'auto' : 'none'};
    box-sizing: border-box;
`;

internals.EmptyStartersContainer = Styled('div', transient$Props)`
    position: absolute;
    top: ${({ $headerHeight }) => `${$headerHeight || 0}px`};
    bottom: ${({ $footerHeight }) => `${$footerHeight || 0}px`};
    left: 0;
    right: 0;
    z-index: 2;
    display: flex;
    overflow-y: auto;
    padding: 16px 20px;
    box-sizing: border-box;
`;

internals.EmptyStartersContent = Styled.div`
    width: 100%;
    max-width: 520px;
    flex-shrink: 0;
    margin: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

internals.EmptyChatInfoPill = Styled.div`
    && {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 28px !important;
        width: fit-content !important;
        max-width: min(520px, calc(100vw - 48px)) !important;
        margin: 0 auto !important;
        padding: 0 12px !important;
        border-radius: 999px !important;
        background: rgba(18, 39, 79, 0.06) !important;
        border: 0 !important;
        box-shadow: none !important;
        text-align: center !important;
        white-space: pre-line !important;
        box-sizing: border-box !important;
    }

    &&,
    && * {
        color: ${({ theme }) => theme.palette.text.primary} !important;
        font-size: 18px !important;
        line-height: 22px !important;
        font-weight: 800 !important;
        letter-spacing: -0.015em !important;
    }

    && > *,
    && p,
    && span,
    && div {
        margin: 0 !important;
        padding: 0 !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
    }

    @media screen and (max-width: 600px) {
        && {
            max-width: calc(100vw - 32px) !important;
        }

        &&,
        && * {
            font-size: 16px !important;
            line-height: 20px !important;
        }
    }
`;

internals.ChatRetryButton = Styled(Button)`
    && {
        margin-left: 12px !important;
        padding: 4px 12px !important;
        font-size: 14px !important;
        line-height: 20px !important;
    }
`;

internals.GifInstructionText = Styled.div`
    text-align: left;
    flex: 1 0 auto;
`;
